import { useContext, useState } from "react"
import { Div, Col, Row, Text } from "atomize"
import { toast } from "react-toastify"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import { theme } from "../settings/config"
import Container from "../atoms/Container"
import Paragraph from "../atoms/Paragraph"
import TextInput from "../inputs/TextInput"
import { motion } from "framer-motion"
import { createPlayer, editPlayer } from "../utils/firestore.utils"
import { schoolContext } from "../providers/school.provider"
import { getPlayerFullName } from "../utils/func.utils"

// Form condiviso tra creazione e modifica di un allievo: i campi sono gli
// stessi, cambia solo cosa succede al salvataggio.
//
// Di un allievo l'app chiede solo come si chiama. Codice fiscale, dati del
// genitore, residenza, recapiti, tessera, taglia e certificato medico non si
// inseriscono più da nessuna parte: sono dati di persone che qui non hanno mai
// messo piede — spesso minorenni — raccolti per un gestionale che questa app
// non è. Quelli già scritti restano leggibili nella scheda dell'allievo, ma
// non se ne aggiungono altri.
export function PlayerFormModal({ mode, closeModal, playerData }) {
	const isEdit = mode === "edit"

	const { selectedSchool } = useContext(schoolContext)

	const [playerName, setPlayerName] = useState(playerData?.playerName ?? "")
	const [playerSurname, setPlayerSurname] = useState(playerData?.playerSurname ?? "")
	const [nickname, setNickname] = useState(playerData?.nickname ?? "")
	const [isSaving, setSavingState] = useState(false)

	const previousFullName = getPlayerFullName(playerData)
	const nextFullName = getPlayerFullName({ playerName, playerSurname, nickname })
	const nameHasChanged = isEdit && previousFullName !== nextFullName

	// Basta un modo per chiamarlo: chi arriva da una lezione segnata al volo
	// spesso ha solo il soprannome, e non deve restare bloccato qui.
	const isDisabled = () => playerName.trim() === "" && nickname.trim() === ""

	// Solo i campi del form: il salvataggio è una updateDoc, quindi quello che
	// non passa di qui resta com'era invece di essere cancellato.
	const buildPlayerData = () => ({
		playerName: playerName.trim(),
		playerSurname: playerSurname.trim(),
		nickname: nickname.trim(),
	})

	const handleSubmit = async () => {
		setSavingState(true)
		try {
			if (isEdit) {
				const { lessonsUpdated } = await editPlayer(playerData.playerID, buildPlayerData(), {
					previousPlayer: playerData,
				})
				toast.success(
					lessonsUpdated > 0
						? `Allievo aggiornato, nome sistemato in ${lessonsUpdated} ${
								lessonsUpdated === 1 ? "lezione" : "lezioni"
							} 🎾`
						: "Allievo aggiornato 🎾",
					{ position: "top-center" },
				)
			} else {
				await createPlayer(buildPlayerData(), selectedSchool)
				toast.success("Allievo aggiunto 🎾", { position: "top-center" })
			}
			closeModal()
		} catch (error) {
			console.error("Errore salvataggio allievo:", error)
			toast.error("Non sono riuscito a salvare l'allievo", { position: "top-center" })
		} finally {
			setSavingState(false)
		}
	}

	return (
		<>
			<Container>
				<Div
					d={"flex"}
					justify="space-between"
					align="center"
					m={{ b: "0.5rem" }}
					textWeight="bold"
					textSize="title"
					textColor={theme === "light" ? "text" : "text_alt"}>
					{isEdit ? "Rinomina allievo" : "Nuovo allievo"}
				</Div>
			</Container>
			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
					<motion.div
						initial={{ opacity: 0, y: 5 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0 }}>
						<Paragraph title="Nome:">
							<TextInput
								value={playerName}
								handleChange={e => setPlayerName(e.target.value)}
							/>
						</Paragraph>
						<Paragraph title="Cognome:">
							<TextInput
								value={playerSurname}
								handleChange={e => setPlayerSurname(e.target.value)}
							/>
						</Paragraph>
						{nameHasChanged && (
							<Text
								textSize="caption"
								textColor={theme.colors.text_light}
								m={{ b: "0.5rem" }}>
								Il nome verrà aggiornato anche nelle lezioni già salvate
							</Text>
						)}
						<Paragraph title="Soprannome:">
							<TextInput
								value={nickname}
								placeholder="come lo chiami in campo"
								handleChange={e => setNickname(e.target.value)}
							/>
						</Paragraph>
						<Text
							textSize="caption"
							textColor={theme.colors.text_light}
							m={{ t: "0.5rem" }}>
							Basta questo per riconoscerlo: quello che non chiediamo non dobbiamo
							custodirlo.
						</Text>
					</motion.div>
				</Container>
			</Div>
			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							text={
								isSaving
									? isEdit
										? "Aggiornando i dati..."
										: "Aggiungendo l'allievo..."
									: "Indietro"
							}
							textSize="subheader"
							disabled={isSaving}
							action={closeModal}
						/>
					</Col>
					{!isSaving && (
						<Col p="0">
							<PrimaryButton
								m="auto"
								bg={theme.colors.primary}
								textColor="white"
								textSize="subheader"
								text="Conferma"
								disabled={isDisabled()}
								action={handleSubmit}
							/>
						</Col>
					)}
				</Row>
			</Div>
		</>
	)
}

export default PlayerFormModal
